import { z } from 'zod';

import { makeApi, Zodios } from '@zodios/core';

import { useAccountContext } from './contexts/AccountContext';
import { useUserContext } from './contexts/UserContext';

export type Application = {
  archived: boolean;
  authMode: AuthMode;
  enabled: boolean;
  entityIdFormat?: EntityIdFormat | undefined;
  id: string;
  name: string;
  oidcEnabled: boolean;
  samlEnabled: boolean;
  trustedApp: boolean;
};
export type AuthMode =
  /**
   * @enum passthrough, plus_service_provider, plus_customer
   */
  "passthrough" | "plus_service_provider" | "plus_customer";
export type EntityIdFormat =
  /**
   * @enum uri, urn
   */
  "uri" | "urn";
export type ApplicationCallbackList = Array<ApplicationCallback>;
export type ApplicationCallback = {
  description?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  id: string;
  key?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  spSamlEntityId?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  spSamlPublicCert?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  uri: string;
};
export type ApplicationDetails = {
  archived: boolean;
  authMode: AuthMode;
  callbacks: Array<ApplicationCallback>;
  defaultSamlBindingMode?: SamlBindingMode | undefined;
  enabled: boolean;
  enforceCallbackKeyAccountId?: /**
   * @default false
   */
  boolean | undefined;
  entityIdFormat?: EntityIdFormat | undefined;
  id: string;
  name: string;
  oauthAccessTokenLifetime: number;
  oauthSecret: string;
  oidcEnabled: boolean;
  openIdAccessTokenLifetime: number;
  openIdRefreshTokenLifetime: number;
  regexCallbacks: boolean;
  samlAttributes: Array<ApplicationSamlCustomAttribute>;
  samlEnabled: boolean;
  samlEncryptedAssertion?: /**
   * @default false
   */
  boolean | undefined;
  samlKeyId: string;
  samlPublicCert: string;
  samlPublicCertExpiration: string;
  samlSignedResponse?: /**
   * @default false
   */
  boolean | undefined;
  samlSpEntityId?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  samlSpPublicCert?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  trustedApp: boolean;
};
export type SamlBindingMode =
  /**
   * @enum post, redirect
   */
  "post" | "redirect";
export type ApplicationSamlCustomAttribute = {
  id: string;
  /**
   * @maxLength 255
   */
  name: string;
  /**
   * @maxLength 255
   */
  nameFormat: string;
  /**
   * @maxLength 255
   */
  valuePath: string;
};
export type ApplicationList = Array<Application>;
export type ApplicationSamlCustomAttributeList =
  Array<ApplicationSamlCustomAttribute>;
export type CreateApplication = {
  authMode: AuthMode;
  enabled: boolean;
  enforceCallbackKeyAccountId?: /**
   * @default false
   */
  boolean | undefined;
  entityIdFormat?: EntityIdFormat | undefined;
  name: string;
  oidcEnabled: boolean;
  regexCallbacks: boolean;
  samlEnabled: boolean;
  samlEncryptedAssertion?: /**
   * @default false
   */
  boolean | undefined;
  samlSignedResponse?: /**
   * @default false
   */
  boolean | undefined;
  samlSpEntityId: (string | null) | Array<string | null>;
  samlSpPublicCert?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  trustedApp: boolean;
};
export type CreateScimIntegration = {
  enabled?: /**
   * @default true
   */
  boolean | undefined;
  idp_hint: ScimIdp;
  idp_other_name?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  name: string;
};
export type ScimIdp =
  /**
   * @enum entra, okta, google_workspace, onelogin, ping_identity, forgerock, sailpoint, saviynt, ibm_verify, oracle_identity, cyberark, beyondtrust, auth_zero, aws_iam_identity_center, rippling, jumpcloud, cisco_duo, shibboleth, workday, bamboohr, adp, sap_successfactors, ukg, other
   */
  | "entra"
  | "okta"
  | "google_workspace"
  | "onelogin"
  | "ping_identity"
  | "forgerock"
  | "sailpoint"
  | "saviynt"
  | "ibm_verify"
  | "oracle_identity"
  | "cyberark"
  | "beyondtrust"
  | "auth_zero"
  | "aws_iam_identity_center"
  | "rippling"
  | "jumpcloud"
  | "cisco_duo"
  | "shibboleth"
  | "workday"
  | "bamboohr"
  | "adp"
  | "sap_successfactors"
  | "ukg"
  | "other";
export type CreateUser = {
  email: string;
  enabled: boolean;
  family_name: string;
  given_name: string;
  preferred_name?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  user_type?: UserType | undefined;
};
export type UserType =
  /**
   * @enum human, api, system
   */
  "human" | "api" | "system";
export type ListResponse_ScimIntegration_ = {
  count: number;
  results: Array<ScimIntegration>;
};
export type ScimIntegration = {
  created_by: UserReference;
  created_timestamp: string;
  enabled: boolean;
  id: string;
  idp_hint: ScimIdp;
  idp_other_name: (string | null) | Array<string | null>;
  modified_by: UserReference;
  modified_timestamp: string;
  name: string;
  tokens: Array<ScimIntegrationToken>;
  version_id: number;
};
export type UserReference = {
  display_name: string;
  id: string;
};
export type ScimIntegrationToken = {
  created_by: UserReference;
  created_timestamp: string;
  id: string;
  last_use_timestamp: (string | null) | Array<string | null>;
  revoked_by: (UserReference | null) | Array<UserReference | null>;
  revoked_timestamp: (string | null) | Array<string | null>;
};
export type ListResponse_User_ = {
  count: number;
  results: Array<User>;
};
export type User = {
  created_by: UserReference;
  created_timestamp: string;
  display_name: string;
  email: (string | null) | Array<string | null>;
  enabled: boolean;
  family_name: (string | null) | Array<string | null>;
  given_name: (string | null) | Array<string | null>;
  id: string;
  modified_by: UserReference;
  modified_timestamp: string;
  org: OrganizationReference;
  preferred_name: (string | null) | Array<string | null>;
  scim_info: (UserScimInfo | null) | Array<UserScimInfo | null>;
  user_type?: UserType | undefined;
  version_id: number;
};
export type OrganizationReference = {
  id: string;
  name: string;
};
export type UserScimInfo = {
  external_id: string;
  scim_integration: ScimIntegrationReference;
};
export type ScimIntegrationReference = {
  id: string;
  name: string;
};
export type ManagementUserList = Array<ManagementUser>;
export type ManagementUser = {
  group_id: string;
  id: string;
};
export type ManagmentGroupList = Array<ManagementGroup>;
export type ManagementGroup = {
  description: string;
  id: string;
  name: string;
  permissions: Array<string>;
};
export type PasswordUserList = Array<PasswordUser>;
export type PasswordUser = {
  /**
   * @maxLength 50
   */
  email: string;
  enabled: boolean;
  id: number;
  id_str: string;
  passwordResetToken?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
};
export type PatchApplication = Partial<{
  /**
   * @default null
   */
  authMode: (AuthMode | null) | Array<AuthMode | null>;
  /**
   * @default null
   */
  enabled: (boolean | null) | Array<boolean | null>;
  /**
   * @default null
   */
  enforceCallbackKeyAccountId: (boolean | null) | Array<boolean | null>;
  /**
   * @default null
   */
  entityIdFormat: (EntityIdFormat | null) | Array<EntityIdFormat | null>;
  /**
   * @default null
   */
  name: (string | null) | Array<string | null>;
  /**
   * @default null
   */
  samlEncryptedAssertion: (boolean | null) | Array<boolean | null>;
  /**
   * @default null
   */
  samlSignedResponse: (boolean | null) | Array<boolean | null>;
  /**
   * @default null
   */
  samlSpEntityId: (string | null) | Array<string | null>;
  /**
   * @default null
   */
  samlSpPublicCert: (string | null) | Array<string | null>;
}>;
export type PatchScimIntegration = Partial<{
  enabled: boolean;
  idp_hint: ScimIdp;
  idp_other_name: (string | null) | Array<string | null>;
  name: string;
}>;
export type PermissionList = Array<Permission>;
export type Permission = {
  action: string;
  description: string;
  entity: string;
  permission: string;
  title: string;
};
export type PutScimIntegration = {
  enabled: boolean;
  idp_hint: ScimIdp;
  idp_other_name?:
    | /**
     * @default null
     */
    ((string | null) | Array<string | null>)
    | undefined;
  name: string;
};
export type ScimIntegrationWithToken = {
  created_by: UserReference;
  created_timestamp: string;
  enabled: boolean;
  id: string;
  idp_hint: ScimIdp;
  idp_other_name: (string | null) | Array<string | null>;
  modified_by: UserReference;
  modified_timestamp: string;
  name: string;
  token: string;
  tokens: Array<ScimIntegrationToken>;
  version_id: number;
};

const AuthMode = z.enum([
  "passthrough",
  "plus_service_provider",
  "plus_customer",
]);
const EntityIdFormat = z.enum(["uri", "urn"]);
const Application: z.ZodType<Application> = z
  .object({
    archived: z.boolean(),
    authMode: AuthMode,
    enabled: z.boolean(),
    entityIdFormat: EntityIdFormat.optional(),
    id: z.string().uuid(),
    name: z.string(),
    oidcEnabled: z.boolean(),
    samlEnabled: z.boolean(),
    trustedApp: z.boolean(),
  })
  .passthrough();
const ApplicationList: z.ZodType<ApplicationList> = z.array(Application);
const ValidationErrorModel = z
  .object({
    ctx: z
      .union([z.object({}).partial().passthrough(), z.null()])
      .optional()
      .default(null),
    input: z.unknown(),
    loc: z.array(z.unknown()),
    msg: z.string(),
    type: z.string(),
    url: z.union([z.string(), z.null()]).optional().default(null),
  })
  .passthrough();
const SamlBindingMode = z.enum(["post", "redirect"]);
const ApplicationSamlCustomAttribute: z.ZodType<ApplicationSamlCustomAttribute> =
  z
    .object({
      id: z.string().uuid(),
      name: z.string().max(255),
      nameFormat: z.string().max(255),
      valuePath: z.string().max(255),
    })
    .passthrough();
const PatchApplicationCallback = z
  .object({
    description: z.union([z.string(), z.null()]).default(null),
    key: z.union([z.string(), z.null()]).default(null),
    spSamlEntityId: z.union([z.string(), z.null()]).default(null),
    spSamlPublicCert: z.union([z.instanceof(File), z.null()]).default(null),
    uri: z.union([z.string(), z.null()]).default(null),
  })
  .partial()
  .passthrough();
const PatchApplicationSamlCustomAttribute = z
  .object({
    name: z.union([z.string(), z.null()]),
    nameFormat: z.union([z.string(), z.null()]),
    valuePath: z.union([z.string(), z.null()]),
  })
  .passthrough();
const CreateApplicationCallback = z
  .object({
    description: z.union([z.string(), z.null()]).optional().default(null),
    key: z.union([z.string(), z.null()]),
    spSamlEntityId: z.union([z.string(), z.null()]).optional().default(null),
    spSamlPublicCert: z
      .union([z.instanceof(File), z.null()])
      .optional()
      .default(null),
    uri: z.string(),
  })
  .passthrough();
const ApplicationSamlCustomAttributeList: z.ZodType<ApplicationSamlCustomAttributeList> =
  z.array(ApplicationSamlCustomAttribute);
const CreateApplicationSamlCustomAttribute = z
  .object({
    name: z.string().max(255),
    nameFormat: z.string().max(255),
    valuePath: z.string().max(255),
  })
  .passthrough();
const ManagementGroup: z.ZodType<ManagementGroup> = z
  .object({
    description: z.string(),
    id: z.string().uuid(),
    name: z.string(),
    permissions: z.array(z.string()),
  })
  .passthrough();
const ManagmentGroupList: z.ZodType<ManagmentGroupList> =
  z.array(ManagementGroup);
const CreateManagementGroup = z
  .object({
    description: z.string(),
    name: z.string(),
    permissions: z.array(z.string()),
  })
  .passthrough();
const UpdateManagementGroup = z
  .object({
    description: z.union([z.string(), z.null()]),
    name: z.union([z.string(), z.null()]),
    permissions: z.union([z.array(z.string()), z.null()]),
  })
  .passthrough();
const Permission: z.ZodType<Permission> = z
  .object({
    action: z.string(),
    description: z.string(),
    entity: z.string(),
    permission: z.string(),
    title: z.string(),
  })
  .passthrough();
const PermissionList: z.ZodType<PermissionList> = z.array(Permission);
const ManagementUser: z.ZodType<ManagementUser> = z
  .object({ group_id: z.string().uuid(), id: z.string().uuid() })
  .passthrough();
const ManagementUserList: z.ZodType<ManagementUserList> =
  z.array(ManagementUser);
const AddManagementUser = z
  .object({ group_id: z.string().uuid(), id: z.string().uuid() })
  .passthrough();
const UpdateManagementUser = z
  .object({ group_id: z.string().uuid() })
  .passthrough();
const PasswordUser: z.ZodType<PasswordUser> = z
  .object({
    email: z.string().max(50),
    enabled: z.boolean(),
    id: z.number().int(),
    id_str: z.string(),
    passwordResetToken: z
      .union([z.string(), z.null()])
      .optional()
      .default(null),
  })
  .passthrough();
const PasswordUserList: z.ZodType<PasswordUserList> = z.array(PasswordUser);
const CreatePasswordUser = z
  .object({
    email: z.string().max(50),
    enabled: z.boolean(),
    plainPassword: z.string(),
  })
  .passthrough();
const PatchPasswordUser = z
  .object({
    email: z.union([z.string(), z.null()]).default(null),
    enabled: z.union([z.boolean(), z.null()]).default(null),
    plainPassword: z.union([z.string(), z.null()]).default(null),
  })
  .partial()
  .passthrough();
const UserReference: z.ZodType<UserReference> = z
  .object({ display_name: z.string(), id: z.string().uuid() })
  .passthrough();
const ScimIdp = z.enum([
  "entra",
  "okta",
  "google_workspace",
  "onelogin",
  "ping_identity",
  "forgerock",
  "sailpoint",
  "saviynt",
  "ibm_verify",
  "oracle_identity",
  "cyberark",
  "beyondtrust",
  "auth_zero",
  "aws_iam_identity_center",
  "rippling",
  "jumpcloud",
  "cisco_duo",
  "shibboleth",
  "workday",
  "bamboohr",
  "adp",
  "sap_successfactors",
  "ukg",
  "other",
]);
const ScimIntegrationToken: z.ZodType<ScimIntegrationToken> = z
  .object({
    created_by: UserReference,
    created_timestamp: z.string().datetime({ offset: true }),
    id: z.string().uuid(),
    last_use_timestamp: z.union([z.string(), z.null()]),
    revoked_by: z.union([UserReference, z.null()]),
    revoked_timestamp: z.union([z.string(), z.null()]),
  })
  .passthrough();
const ScimIntegration: z.ZodType<ScimIntegration> = z
  .object({
    created_by: UserReference,
    created_timestamp: z.string().datetime({ offset: true }),
    enabled: z.boolean(),
    id: z.string().uuid(),
    idp_hint: ScimIdp,
    idp_other_name: z.union([z.string(), z.null()]),
    modified_by: UserReference,
    modified_timestamp: z.string().datetime({ offset: true }),
    name: z.string(),
    tokens: z.array(ScimIntegrationToken),
    version_id: z.number().int(),
  })
  .passthrough();
const ListResponse_ScimIntegration_: z.ZodType<ListResponse_ScimIntegration_> =
  z
    .object({ count: z.number().int(), results: z.array(ScimIntegration) })
    .passthrough();
const CreateScimIntegration: z.ZodType<CreateScimIntegration> = z
  .object({
    enabled: z.boolean().optional().default(true),
    idp_hint: ScimIdp,
    idp_other_name: z.union([z.string(), z.null()]).optional().default(null),
    name: z.string(),
  })
  .passthrough();
const ScimIntegrationWithToken: z.ZodType<ScimIntegrationWithToken> = z
  .object({
    created_by: UserReference,
    created_timestamp: z.string().datetime({ offset: true }),
    enabled: z.boolean(),
    id: z.string().uuid(),
    idp_hint: ScimIdp,
    idp_other_name: z.union([z.string(), z.null()]),
    modified_by: UserReference,
    modified_timestamp: z.string().datetime({ offset: true }),
    name: z.string(),
    token: z.string(),
    tokens: z.array(ScimIntegrationToken),
    version_id: z.number().int(),
  })
  .passthrough();
const PatchScimIntegration: z.ZodType<PatchScimIntegration> = z
  .object({
    enabled: z.boolean(),
    idp_hint: ScimIdp,
    idp_other_name: z.union([z.string(), z.null()]),
    name: z.string(),
  })
  .partial()
  .passthrough();
const PutScimIntegration: z.ZodType<PutScimIntegration> = z
  .object({
    enabled: z.boolean(),
    idp_hint: ScimIdp,
    idp_other_name: z.union([z.string(), z.null()]).optional().default(null),
    name: z.string(),
  })
  .passthrough();
const X_WDC_Org = z.union([z.string(), z.null()]).optional().default(null);
const OrganizationReference: z.ZodType<OrganizationReference> = z
  .object({ id: z.string().uuid(), name: z.string() })
  .passthrough();
const ScimIntegrationReference: z.ZodType<ScimIntegrationReference> = z
  .object({ id: z.string().uuid(), name: z.string() })
  .passthrough();
const UserScimInfo: z.ZodType<UserScimInfo> = z
  .object({
    external_id: z.string(),
    scim_integration: ScimIntegrationReference,
  })
  .passthrough();
const UserType = z.enum(["human", "api", "system"]);
const User: z.ZodType<User> = z
  .object({
    created_by: UserReference,
    created_timestamp: z.string().datetime({ offset: true }),
    display_name: z.string(),
    email: z.union([z.string(), z.null()]),
    enabled: z.boolean(),
    family_name: z.union([z.string(), z.null()]),
    given_name: z.union([z.string(), z.null()]),
    id: z.string().uuid(),
    modified_by: UserReference,
    modified_timestamp: z.string().datetime({ offset: true }),
    org: OrganizationReference,
    preferred_name: z.union([z.string(), z.null()]),
    scim_info: z.union([UserScimInfo, z.null()]),
    user_type: UserType.optional(),
    version_id: z.number().int(),
  })
  .passthrough();
const ListResponse_User_: z.ZodType<ListResponse_User_> = z
  .object({ count: z.number().int(), results: z.array(User) })
  .passthrough();
const CreateUser: z.ZodType<CreateUser> = z
  .object({
    email: z.string(),
    enabled: z.boolean(),
    family_name: z.string(),
    given_name: z.string(),
    preferred_name: z.union([z.string(), z.null()]).optional().default(null),
    user_type: UserType.optional(),
  })
  .passthrough();

export const schemas = {
  AuthMode,
  EntityIdFormat,
  Application,
  ApplicationList,
  ValidationErrorModel,
  SamlBindingMode,
  ApplicationSamlCustomAttribute,
  PatchApplicationCallback,
  PatchApplicationSamlCustomAttribute,
  CreateApplicationCallback,
  ApplicationSamlCustomAttributeList,
  CreateApplicationSamlCustomAttribute,
  ManagementGroup,
  ManagmentGroupList,
  CreateManagementGroup,
  UpdateManagementGroup,
  Permission,
  PermissionList,
  ManagementUser,
  ManagementUserList,
  AddManagementUser,
  UpdateManagementUser,
  PasswordUser,
  PasswordUserList,
  CreatePasswordUser,
  PatchPasswordUser,
  UserReference,
  ScimIdp,
  ScimIntegrationToken,
  ScimIntegration,
  ListResponse_ScimIntegration_,
  CreateScimIntegration,
  ScimIntegrationWithToken,
  PatchScimIntegration,
  PutScimIntegration,
  X_WDC_Org,
  OrganizationReference,
  ScimIntegrationReference,
  UserScimInfo,
  UserType,
  User,
  ListResponse_User_,
  CreateUser,
};

export const Management_GroupsEndpoints = makeApi([
  {
    method: "get",
    path: "/api/v1/management-groups",
    alias: "api_v1_management_groups_list_api_v1_management_groups_get",
    requestFormat: "json",
    response: z.array(ManagementGroup),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/api/v1/management-groups",
    alias: "api_v1_management_groups_create_api_v1_management_groups_post",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateManagementGroup,
      },
    ],
    response: ManagementGroup,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "delete",
    path: "/api/v1/management-groups/:id",
    alias:
      "api_v1_management_groups_delete_api_v1_management_groups__int_id__delete",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/management-groups/:id",
    alias:
      "api_v1_management_groups_get_api_v1_management_groups__uuid_id__get",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ManagementGroup,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "patch",
    path: "/api/v1/management-groups/:id",
    alias:
      "api_v1_management_groups_patch_api_v1_management_groups__uuid_id__patch",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: UpdateManagementGroup,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ManagementGroup,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useManagement_GroupsApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_MA_API, Management_GroupsEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WDC-Org": accountContext.accountId,
      },
    },
  });
};

export const Management_PermissionsEndpoints = makeApi([
  {
    method: "get",
    path: "/api/v1/management-permissions",
    alias: "get_management_permissions_list",
    requestFormat: "json",
    response: z.array(Permission),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useManagement_PermissionsApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(
    import.meta.env.VITE_MA_API,
    Management_PermissionsEndpoints,
    {
      axiosConfig: {
        headers: {
          Authorization: `Bearer ${userContext.accessToken}`,
          "X-WDC-Org": accountContext.accountId,
        },
      },
    }
  );
};

export const Management_UsersEndpoints = makeApi([
  {
    method: "get",
    path: "/api/v1/management-users",
    alias: "api_v1_management_users_list_api_v1_management_users_get",
    requestFormat: "json",
    response: z.array(ManagementUser),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/api/v1/management-users",
    alias: "api_v1_management_users_create_api_v1_management_users_post",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: AddManagementUser,
      },
    ],
    response: ManagementUser,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "delete",
    path: "/api/v1/management-users/:id",
    alias:
      "api_v1_management_users_delete_api_v1_management_users__int_id__delete",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/management-users/:id",
    alias: "api_v1_management_users_get_api_v1_management_users__uuid_id__get",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ManagementUser,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "put",
    path: "/api/v1/management-users/:id",
    alias: "api_v1_management_users_put_api_v1_management_users__uuid_id__put",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: z.object({ group_id: z.string().uuid() }).passthrough(),
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ManagementUser,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useManagement_UsersApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_MA_API, Management_UsersEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WDC-Org": accountContext.accountId,
      },
    },
  });
};

export const Password_UsersEndpoints = makeApi([
  {
    method: "get",
    path: "/api/v1/password-users",
    alias: "api_v1_password_users_list_api_v1_password_users_get",
    requestFormat: "json",
    response: z.array(PasswordUser),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/api/v1/password-users",
    alias: "api_v1_password_users_create_api_v1_password_users_post",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreatePasswordUser,
      },
    ],
    response: PasswordUser,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "delete",
    path: "/api/v1/password-users/:id",
    alias: "api_v1_password_users_delete_api_v1_password_users__int_id__delete",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/password-users/:id",
    alias: "api_v1_password_users_get_api_v1_password_users__int_id__get",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: PasswordUser,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "patch",
    path: "/api/v1/password-users/:id",
    alias: "api_v1_password_users_patch_api_v1_password_users__int_id__patch",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchPasswordUser,
      },
      {
        name: "id",
        type: "Path",
        schema: z.number().int(),
      },
    ],
    response: PasswordUser,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const usePassword_UsersApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_MA_API, Password_UsersEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WDC-Org": accountContext.accountId,
      },
    },
  });
};

export const SCIM_IntegrationsEndpoints = makeApi([
  {
    method: "get",
    path: "/api/v1/scim-integrations",
    alias: "list_scim_integrations",
    requestFormat: "json",
    response: ListResponse_ScimIntegration_,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/api/v1/scim-integrations",
    alias: "create_scim_integration",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateScimIntegration,
      },
    ],
    response: ScimIntegrationWithToken,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "delete",
    path: "/api/v1/scim-integrations/:id",
    alias: "delete_scim_integration_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/scim-integrations/:id",
    alias: "get_scim_integration_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ScimIntegration,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "patch",
    path: "/api/v1/scim-integrations/:id",
    alias: "patch_scim_integration_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PatchScimIntegration,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ScimIntegration,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "put",
    path: "/api/v1/scim-integrations/:id",
    alias: "put_scim_integration_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: PutScimIntegration,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ScimIntegration,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/api/v1/scim-integrations/:id/rekey",
    alias: "rekey_scim_integration_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: ScimIntegrationWithToken,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "delete",
    path: "/api/v1/scim-integrations/:integration_id/tokens/:token_id",
    alias: "delete_scim_integration_token_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "integration_id",
        type: "Path",
        schema: z.string().uuid(),
      },
      {
        name: "token_id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useSCIM_IntegrationsApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_MA_API, SCIM_IntegrationsEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WDC-Org": accountContext.accountId,
      },
    },
  });
};

export const Token_KeysEndpoints = makeApi([
  {
    method: "post",
    path: "/api/v1/token-keys/generate",
    alias: "api_v1_token_keys_generate_api_v1_token_keys_generate_post",
    requestFormat: "json",
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useToken_KeysApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_MA_API, Token_KeysEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WDC-Org": accountContext.accountId,
      },
    },
  });
};

export const UsersEndpoints = makeApi([
  {
    method: "get",
    path: "/api/v1/users",
    alias: "list_users",
    requestFormat: "json",
    parameters: [
      {
        name: "X-WDC-Org",
        type: "Header",
        schema: X_WDC_Org,
      },
    ],
    response: ListResponse_User_,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "post",
    path: "/api/v1/users",
    alias: "create_user",
    requestFormat: "json",
    parameters: [
      {
        name: "body",
        type: "Body",
        schema: CreateUser,
      },
      {
        name: "X-WDC-Org",
        type: "Header",
        schema: X_WDC_Org,
      },
    ],
    response: User,
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "delete",
    path: "/api/v1/users/:id",
    alias: "delete_user_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "X-WDC-Org",
        type: "Header",
        schema: X_WDC_Org,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: z.void(),
    errors: [
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "get",
    path: "/api/v1/users/:id",
    alias: "get_user_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "X-WDC-Org",
        type: "Header",
        schema: X_WDC_Org,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: User,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "patch",
    path: "/api/v1/users/:id",
    alias: "patch_user_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "X-WDC-Org",
        type: "Header",
        schema: X_WDC_Org,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: User,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
  {
    method: "put",
    path: "/api/v1/users/:id",
    alias: "update_user_by_id",
    requestFormat: "json",
    parameters: [
      {
        name: "X-WDC-Org",
        type: "Header",
        schema: X_WDC_Org,
      },
      {
        name: "id",
        type: "Path",
        schema: z.string().uuid(),
      },
    ],
    response: User,
    errors: [
      {
        status: 404,
        description: `Not Found`,
        schema: z.void(),
      },
      {
        status: 422,
        description: `Unprocessable Content`,
        schema: z.array(ValidationErrorModel),
      },
    ],
  },
]);

export const useUsersApi = () => {
  const userContext = useUserContext();
  const accountContext = useAccountContext();

  return new Zodios(import.meta.env.VITE_MA_API, UsersEndpoints, {
    axiosConfig: {
      headers: {
        Authorization: `Bearer ${userContext.accessToken}`,
        "X-WDC-Org": accountContext.accountId,
      },
    },
  });
};
